import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { isProTier } from '@/lib/tier';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, Sparkles, Zap, ArrowRight, AlertTriangle, RefreshCw } from 'lucide-react';

// Mollie statuses that are still in progress (keep polling) vs terminal-failed.
const PENDING_STATUSES = new Set(['open', 'pending', 'authorized', 'checking']);
const MAX_TRIES = 6;

export default function BillingReturn() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [result, setResult] = useState('checking'); // checking | paid | failed
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;

    const check = async () => {
      try {
        const { data } = await api.get('/billing/verify');
        if (cancelled) return;
        setInfo(data);
        await refreshUser();
        if (data.status === 'paid') {
          setResult('paid');
        } else if (PENDING_STATUSES.has(data.status) && tries < MAX_TRIES) {
          tries += 1;
          setTimeout(check, 1500);
        } else {
          // Terminal non-paid status (canceled/failed/expired/none) or polling exhausted.
          setResult('failed');
        }
      } catch (e) {
        if (cancelled) return;
        tries += 1;
        if (tries < MAX_TRIES) setTimeout(check, 1500);
        else setResult('failed');
      }
    };
    check();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kind = info?.kind;
  const isCredits = kind === 'credits';
  const isPro = info?.is_pro || isProTier(user?.subscription_tier);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md kite-card p-8 text-center" data-testid="billing-return">
        {result === 'checking' && (
          <>
            <Loader2 className="w-10 h-10 text-[#00B4D8] animate-spin mx-auto" />
            <h1 className="font-display text-3xl mt-5">{t('billing.confirming')}</h1>
            <p className="text-muted-foreground mt-2 text-sm">{t('billing.confirmingSub')}</p>
          </>
        )}

        {result === 'failed' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-[#FF6B35]/12 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-9 h-9 text-[#FF6B35]" />
            </div>
            <h1 className="font-display text-4xl mt-5" data-testid="billing-return-failed-title">
              {t('billing.notCompleted')}
            </h1>
            <p className="text-muted-foreground mt-2" data-testid="billing-return-failed-desc">
              {t('billing.notCompletedDesc')}
            </p>
            <div className="mt-7 flex flex-col gap-2">
              <Button onClick={() => navigate('/pricing')} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="billing-return-retry">
                <RefreshCw className="w-4 h-4 mr-2" /> {t('billing.tryAgain')}
              </Button>
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-white" data-testid="billing-return-dashboard">
                {t('billing.goToDashboard')}
              </Button>
            </div>
          </>
        )}

        {result === 'paid' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-[#2EE59D]/12 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-[#2EE59D]" />
            </div>
            <h1 className="font-display text-4xl mt-5" data-testid="billing-return-title">
              {isCredits ? t('billing.creditsAdded') : t('billing.welcomePro')}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isCredits ? t('billing.creditsAddedDesc') : t('billing.welcomeProDesc')}
            </p>

            <div className="mt-6 flex items-center justify-center gap-3">
              <div className="rounded-xl bg-[#0A0A0A] border border-white/10 px-5 py-3">
                <div className="label-eyebrow text-muted-foreground flex items-center justify-center gap-1"><Zap className="w-3.5 h-3.5 text-[#00B4D8]" /> {t('billing.credits')}</div>
                <div className="font-data text-2xl font-semibold text-[#00B4D8] mt-1" data-testid="billing-return-credits">{info?.credits ?? user?.credits ?? '—'}</div>
              </div>
              {isPro && (
                <div className="rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/30 px-5 py-3">
                  <div className="label-eyebrow text-muted-foreground">{t('billing.plan')}</div>
                  <div className="font-display text-2xl text-[#FF6B35] mt-1">PRO</div>
                </div>
              )}
            </div>

            {info?.demo && <p className="text-xs text-muted-foreground mt-4">{t('billing.testModeNoCharge')}</p>}

            <div className="mt-7 flex flex-col gap-2">
              <Button onClick={() => navigate('/analyze')} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="billing-return-analyze">
                <Sparkles className="w-4 h-4 mr-2" /> {t('billing.analyzeSession')}
              </Button>
              {isPro && (
                <Button variant="outline" onClick={() => navigate('/gear-advisor')} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl" data-testid="billing-return-gear-advisor">
                  {t('billing.tryGearAdvisor')} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-white">{t('billing.goToDashboard')}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
